import styled from "@emotion/styled";
import theme from "../../theme";
import PopUpMsg from "../popUpMsg";
import Spinner from "../spinner";
import { formatDate, handleKeyDown } from "../../utils/util";
import { ReactNode, useEffect, useState } from "react";
import { CustomListType, MobilityReportByUser } from "../../types/types";
import { Button } from "../common/Button";
import { useWindowSize } from "@reactuses/core";
import { Pagination } from "@mui/material";
import { EventTag } from "../EventTag";
import { useI18n } from "../../locales";

const { sm, md, lg } = theme.screen;

type Prop = {
  listRef?: React.RefObject<HTMLDivElement>;
  selectedRef?: React.RefObject<HTMLDivElement>;
  reportData?: CustomListType<MobilityReportByUser[]>;
  reportDataIsLoading: boolean;
  selectedReport: MobilityReportByUser | null;
  handleClickReportList: (el: MobilityReportByUser) => void;
  noData: boolean;
  children: ReactNode;
  reportPage: number;
  setReportPage: React.Dispatch<React.SetStateAction<number>>;
  currentLocation: string;
};

export const OperationHistory = ({
  listRef,
  selectedRef,
  reportData,
  reportDataIsLoading,
  selectedReport,
  handleClickReportList,
  noData,
  children,
  reportPage,
  setReportPage,
  currentLocation,
}: Prop) => {
  const { width } = useWindowSize();
  const { t } = useI18n();

  // 백엔드에서 받은 status 값을 언어팩 값으로 변환하는 함수
  const getStatusText = (status: string): string => {
    // 백엔드에서 받은 값이 언어팩 값과 일치하는지 확인
    if (status === t.control.operationRunning) {
      return t.control.operationRunning;
    }
    if (status === t.control.operationEnd) {
      return t.control.operationEnd;
    }
    
    // 한국어 기본값과 비교 (백엔드가 한국어로 보낼 수 있음)
    if (status === "운행중") {
      return t.control.operationRunning;
    }
    if (status === "운행종료") {
      return t.control.operationEnd;
    }
    
    // 매칭되지 않으면 원본 값 반환
    return status;
  };
  const [reportDataList, setReportDataList] = useState<
    MobilityReportByUser[]
  >([]); // 리포트 데이터 리스트
  // const [endAddressList, setEndAddressList] = useState<
  //   MobilityReportByUser[]
  // >([]); // 운행 종료 위치 좌표를 이용해 도로명 주소로 변환한 리포트 데이터 리스트
  const [active, setActive] = useState<"history" | "map" | "">("");

  // 리포트 데이터 리스트 초기화
  useEffect(() => {
    if (!reportData) return;
    if (reportDataList.length > 0 && reportData.list.length > 0) {
      if (reportDataList[0].device_pid !== reportData.list[0].device_pid) {
        // setEndAddressList([]);
        return setReportDataList(reportData.list);
      }
    }
    if (reportData && reportData.list.length > 0) {
      // setEndAddressList([]);
      return setReportDataList(reportData.list);
    }
  }, [reportData]);

  // 화면 사이즈 체크하여 버튼 활성화할지 확인
  useEffect(() => {
    if (width > lg) {
      setActive("");
    } else {
      setActive("history");
    }
  }, [width]);

  // 화면 사이즈가 1280 이하일때
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;
    if (width <= lg) {
      if (noData) {
        timeoutId = setTimeout(() => {
          setActive("history");
        }, 1100);
      }
    }
    return () => timeoutId && clearTimeout(timeoutId);
  }, [width, noData]);

  // 최근 운행내역으로 돌아갔을때 선택된 내역으로 스크롤 이동
  useEffect(() => {
    if (width <= lg) {
      if (active === "history") {
        selectedRef?.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [width, active]);

  // 리포트 데이터 받아와지면 최근 운행 내역 영역으로 스크롤 이동 lg이하
  useEffect(() => {
    if (!reportData) return;
    if (reportData?.list.length > 0 && width <= lg) {
      listRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [reportData, width]);

  const handlePageChange = (
    // 페이지네이션
    _: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setReportPage(value - 1);
  };

  // console.log("reportData", reportData);

  return (
    <WrapperStyledDiv>
      <PopUpMsg
        open={noData}
        content="해당 데이터를 확인할 수 없습니다."
      />
      <SubTitleStyledH2>
        {t.control.recentOperationHistory}
        {width <= lg && (
          <Button
            bgColor={theme.color.green_500}
            hoverColor={theme.color.green_600}
            onClick={() => {
              if (active === "history") {
                setActive("map");
                return;
              }
              if (active === "map") {
                setActive("history");
              }
            }}
            style={{
              height: "35px",
              fontSize: "12px",
            }}
          >
            {active === "history" ? t.control.viewMap : t.control.viewOperationHistory}
          </Button>
        )}
      </SubTitleStyledH2>

      <InnerWrapperStyledDiv>
        {active !== "map" && (
          <ReportWrapper>
            <ReportLists ref={listRef}>
              {reportDataIsLoading && (
                <Spinner width={"50px"} height={"50px"} />
              )}
              {reportData?.list.length !== 0 ? (
                reportData?.list.map((el) => {
                  return (
                    <ReportList
                      ref={
                        el.running_seq === selectedReport?.running_seq
                          ? selectedRef
                          : null
                      }
                      onClick={() => {
                        if (
                          el.running_seq === selectedReport?.running_seq
                        ) {
                          return;
                        } else {
                          handleClickReportList(el);

                          // 화면 사이즈가 1280 이하면 지도 활성화로 바꾸기
                          if (width <= lg) {
                            setActive("map");
                          }
                        }
                      }}
                      key={el.running_seq}
                      active={
                        selectedReport?.running_seq === el.running_seq
                      }
                      role="button"
                      tabIndex={0}
                      onKeyDown={handleKeyDown}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "7px",
                        }}
                      >
                        <WorkStartDateStyledDiv>
                          {formatDate(el.work_start, 'yyyy-MM-dd')}
                        </WorkStartDateStyledDiv>
                        <StatusCircleStyledDiv
                          bgColor={
                            getStatusText(el.status) === t.control.operationEnd
                              ? `${theme.color.neutral_400}`
                              : `${theme.color.green_500}`
                          }
                        />
                        <StatusStyledSpan>{getStatusText(el.status)}</StatusStyledSpan>
                        {el.events && (
                          <EventTag
                            text={
                              el.events.includes("차량전복")
                                ? el.events.length > 1
                                  ? `차량전복 외 ${el.events.length - 1}건`
                                  : "차량전복"
                                : el.events.length > 1
                                ? `${el.events[0]} 외 ${
                                    el.events.length - 1
                                  }건`
                                : el.events[0]
                            }
                            color={
                              el.events.includes("차량전복")
                                ? "red"
                                : "orange"
                            }
                          />
                        )}
                      </div>

                      <WorkTimeTextStyledDiv>
                        {(() => {
                          const start = new Date(el.work_start).getTime();
                          const end = new Date(el.work_end).getTime();
                          const totalSeconds = Math.max(0, Math.floor((end - start) / 1000));
                          const h = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
                          const m = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
                          const s = String(totalSeconds % 60).padStart(2, '0');
                          return `${h}:${m}:${s}`;
                        })()}
                        [{formatDate(el.work_start, "HH:mm:ss")} ~ {formatDate(String(el.work_end), "HH:mm:ss")}] 
                      </WorkTimeTextStyledDiv>
                      <WorkDistanceTextStyledDiv>
                        {t.control.travelDistance}: {el.cumulative_distance ?? 0} km
                      </WorkDistanceTextStyledDiv>

                      <div>
                        <EndPointStyledSpan>
                          {t.control.operationEndLocation}
                          {getStatusText(el.status) === t.control.operationRunning &&
                            (currentLocation
                              ? currentLocation
                              : t.control.addressUnavailable ?? '주소를 확인할 수 없습니다.')}
                          {getStatusText(el.status) === t.control.operationEnd &&
                            (el.end_road
                              ? el.end_road
                              : t.control.addressUnavailable ?? '주소를 확인할 수 없습니다.')}
                        </EndPointStyledSpan>
                      </div>
                    </ReportList>
                  );
                })
              ) : (
                <NoReportData>{t.control.noRecentOperationHistory}</NoReportData>
              )}
            </ReportLists>
            {reportData && reportData.list.length > 0 && (
              <Pagination
                page={reportPage + 1}
                count={reportData?.total_page}
                onChange={handlePageChange}
                siblingCount={width > lg ? 3 : width > md ? 4 : 2}
                size="small"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  justifySelf: "end",
                  width: "100%",
                }}
              />
            )}
          </ReportWrapper>
        )}

        {/* 지도 영역 */}
        {active !== "history" && children}
      </InnerWrapperStyledDiv>
    </WrapperStyledDiv>
  );
};

const WrapperStyledDiv = styled.div`
  background: ${theme.color.neutral_50};
  border: 3px solid ${theme.color.neutral_100};
  border-radius: 15px;
  height: 583px;
  padding-top: 20px;
  display: flex;
  flex-direction: column;
  margin-top: -10px;
  overflow: hidden;

  @media (max-width: ${lg}px) {
    height: 600px;
  }
  @media print {
    max-height: 583px;
  }
`;

const SubTitleStyledH2 = styled.h2`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 700;
  font-size: 15px;
  line-height: 18px;
  color: ${theme.color.neutral_600};
  padding-left: 20px;
  padding-right: 20px;
  border-bottom: 3px solid ${theme.color.neutral_200};
  padding-bottom: 20px;
`;

const InnerWrapperStyledDiv = styled.div`
  height: 516px;
  display: flex;
`;

const ReportWrapper = styled.div`
  height: 516px;

  @media (max-width: ${lg}px) {
    flex: 1;
  }
  @media print {
    max-width: 420px;
    max-height: 516px;
  }
`;

const ReportLists = styled.div`
  width: 420px;
  height: 490px;
  border: 1px solid ${theme.color.neutral_100};
  /* border-bottom-right-radius: 20px;
  border-bottom-left-radius: 15px; */
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  @media (max-width: ${lg}px) {
    width: 100%;
  }
  @media print {
    nav {
      max-width: 420px;
    }
  }
`;

const ReportList = styled.div<{ active: boolean }>`
  padding: 20px 30px 20px 30px;
  border-bottom: 1px solid ${theme.color.neutral_200};
  cursor: pointer;
  background: ${({ active }) =>
    active ? `${theme.color.neutral_100}` : `${theme.color.white}`};

  &:hover {
    background-color: ${theme.color.neutral_100};
  }

  @media (max-width: ${sm}px) {
    padding: 15px;
  }
`;

const WorkStartDateStyledDiv = styled.span`
  font-weight: 700;
  font-size: 16px;
  line-height: 19px;
  margin-right: 10px;
  color: ${theme.color.neutral_700};

  @media (max-width: ${sm}px) {
    font-size: 14px;
  }
`;

const StatusCircleStyledDiv = styled.div<{ bgColor: string }>`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background-color: ${({ bgColor }) => bgColor};
  margin-right: 5px;
`;

const StatusStyledSpan = styled.span`
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  color: ${theme.color.neutral_600};

  @media (max-width: ${sm}px) {
    font-size: 12px;
  }
`;

const WorkTimeTextStyledDiv = styled.div`
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: ${theme.color.neutral_600};
  margin-bottom: 3px;
`;

const WorkDistanceTextStyledDiv = styled.div`
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: ${theme.color.neutral_600};
  margin-bottom: 3px;
`;

const EndPointStyledSpan = styled.span`
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  color: ${theme.color.neutral_500};
`;

const NoReportData = styled.p`
  font-weight: 500;
  font-size: 15px;
  line-height: 22px;
  color: ${theme.color.neutral_500};
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
